import { Chain, Environment } from '@shared/data-access/interfaces';
import { Observable, of, timer } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { AuthenticationService } from '@shared/authentication/data-access';
import { CanActivate } from '@angular/router';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private readonly chain: Chain,
    private readonly authService: AuthenticationService,
    private readonly environment: Environment
  ) {}

  canActivate(): Observable<boolean> {
    const useAuthGuardSetInterval =
      this.environment.killSwitch?.useAuthGuardSetInterval?.[
        this.chain.chainIdentifierAbbr
      ] ?? false;

    const isAuthenticatedObs = useAuthGuardSetInterval
      ? this.waitForAuthFunction().pipe(
          switchMap(() => this.authService.getIsAuthenticated$())
        )
      : this.authService.getIsAuthenticated$();

    return isAuthenticatedObs.pipe(
      map((loggedIn: boolean) => {
        if (!loggedIn) {
          this.authService.login({
            target: this.chain.myAccountUrlLoggedIn,
          });
        }
        return loggedIn;
      }),
      first() // Ensure we only emit once for the route activation
    );
  }

  private waitForAuthFunction(): Observable<void> {
    const checkIntervalMs = 100;
    const timeoutMs = 1000;

    return timer(0, checkIntervalMs).pipe(
      map(() => typeof window.authFunctions?.isAuthenticated === 'function'),
      first((isAvailable) => isAvailable),
      map(() => undefined), // We only care that it's available
      catchError(() => {
        console.error('authFunctions.isAuthenticated function not available after waiting.');
        return of(undefined); // Or throw an error if availability is critical
      }),
      first(() => true, timer(timeoutMs)) // Timeout if not available within the specified time
    );
  }
}
